import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import RegainedGuestResults from 'features/Analytics/Conversions/RegainedGuestResults'
import { useEffect, useState } from 'react'
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts'
import performanceReportsApi from 'services/api/performanceReports'
import { formatNumberAsThousands } from 'utils/priceHelpers'

import styles from './Conversions.module.scss'
import { ReactComponent as LinkNewTabIcon } from './linkNewTab.svg'

const Square = props => (
  <svg
    className={styles.regainedGuestsListItemBox}
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="#F0EAF0"
    {...props}
  >
    <rect x="0" y="0" width="18" height="18" />
  </svg>
)

const barColors = ['#4338CA', '#2563EB', '#0EA5E9', '#22D3EE', '#5eead4']

const RegainedGuestsChart = ({ kind, fromDate, toDate, hideBreakdownLinks = false }) => {
  const [isLoading, setIsLoading] = useState(true)
  const [isErred, setIsErred] = useState()
  const [data, setData] = useState([])

  useEffect(() => {
    setIsLoading(true)

    const abortController = new AbortController()

    performanceReportsApi.lost.segmentations
      .overview({ fromDate, toDate, kind }, abortController)
      .then(data => {
        setIsErred(false)
        setData(data)
      })
      .catch(err => {
        if (!err.toString().includes('The user aborted a request.')) {
          setIsErred(true)
        }
      })
      .finally(() => {
        setIsLoading(false)
      })

    return () => abortController.abort()
  }, [fromDate, toDate, kind])

  const [hoveredEntry, setHoveredEntry] = useState(null)

  const [activeSegment, setActiveSegment] = useState(null)

  return (
    <>
      {activeSegment ? (
        <RegainedGuestResults
          category={activeSegment}
          onClose={() => setActiveSegment(null)}
          fromDate={fromDate}
          toDate={toDate}
          kind={kind}
          segment={activeSegment}
        />
      ) : null}
      <div className={styles.regainedGuestsCard}>
        <h3 className={styles.title}>Regained lost guests</h3>
        {isLoading && <LoadingIndicator />}
        {isErred && (
          <div className={styles.error}>
            <p>There was an error loading the data. Please try again later.</p>
          </div>
        )}
        {!isLoading && !isErred && (
          <div
            className={styles.regainedGuestsCardContent}
            style={{ position: 'relative' }}
          >
            <div className={styles.regainedGuestsPieWrapper}>
              <div className={styles.regainedGuestsPieContent}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={data}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius="72%"
                      outerRadius="93%"
                      fill="#82ca9d"
                      direction="clockwise"
                    >
                      {data.map((entry, index) => (
                        <Cell
                          key={entry.name}
                          onMouseEnter={() => setHoveredEntry(entry.name)}
                          onMouseLeave={() => setHoveredEntry(null)}
                          fill={barColors[index % barColors.length]}
                          style={{
                            zIndex: hoveredEntry === entry.name ? 1 : 0,
                            opacity: hoveredEntry
                              ? hoveredEntry === entry.name
                                ? 1
                                : 0.15
                              : 1,
                            filter:
                              hoveredEntry === entry.name
                                ? `drop-shadow(0px 0 15px rgba(0, 0, 0, 0.12)) brightness(1.05)`
                                : 'grayscale(0.20)'
                          }}
                        />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className={styles.regainedGuestsPieMiddleText}>
                  <p className={styles.regainedGuestsPieMiddleTextTitle}>
                    {data.reduce((prev, curr) => prev + curr.value, 0)}{' '}
                  </p>
                  <p className={styles.regainedGuestsPieMiddleTextLabel}>
                    guests regained
                  </p>
                </div>
              </div>
            </div>
            <div className={styles.regainedGuestsList}>
              <p className={styles.regainedGuestsListTitle}>Months since last stay</p>
              <ul>
                {data.map(({ name, value }, index) => (
                  <li
                    key={`regained-guests-list-item-${index}`}
                    onMouseEnter={() => setHoveredEntry(name)}
                    onMouseLeave={() => setHoveredEntry(null)}
                    onClick={() => (hideBreakdownLinks ? null : setActiveSegment(name))}
                  >
                    <div
                      className={
                        hoveredEntry === name
                          ? styles.regainedGuestsListItemHovered
                          : styles.regainedGuestsListItem
                      }
                    >
                      <Square fill={barColors[index]} />
                      <p className={styles.regainedGuestsListItemCategory}>
                        <span className={styles.regainedGuestsListItemCategoryName}>
                          {name}
                        </span>
                        {!hideBreakdownLinks && (
                          <>
                            <span className={styles.regainedGuestsListItemHoverTip}>
                              See guest list
                            </span>
                          </>
                        )}
                      </p>
                      <p className={styles.regainedGuestsListItemCount}>
                        {formatNumberAsThousands(value)}
                        <LinkNewTabIcon
                          className={styles.regainedGuestsListLinkIcon}
                          fill="#0079ff"
                          width={16}
                          height={16}
                        />
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default RegainedGuestsChart
